// @flow
import React from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Main } from '@finect/tabular-core/Main';
import { Content } from '@finect/tabular-core/Content';
import CookiesCMP from '@finect/front-core/CookiesCMP';
import { NotFound } from '@finect/tabular-components/NotFound';
import { ErrorMessage } from '@finect/tabular-components/ErrorMessage';
import { Footer } from '@finect/tabular-components/Footer';
import { finect as theme } from '@finect/tabular-themes/finect';
import { reset } from '@finect/tabular-utils/reset';
import { OmniNav } from '@finect/tabular-omninav';
import { getRelativeParent } from '@finect/front-utils/getRelativeParent';
import { TrackableProvider } from '@finect/front-gtm';
import { getLoggedUser, fetchUserNotifications } from '@finect/front-core/User';
import GoogleSignIn from '@finect/front-core/GoogleSignIn';
import { InterstitialWithTargeting } from '@finect/tabular-components/Ads';
import { Questions, Question, NewQuestion, Topic, Topics } from './scenes';
import { openOverlay } from './store/overlay/actions';
import { discardError } from './store/error/actions';
import { getLiveEvents } from './store/liveEvents/actions';
import { AppContextual } from './components/AppContextual';
import { Overlay } from './components';
import type { StoreType, UserType, ErrorType } from './types';

const mapStateToProps = (state: StoreType) => ({
  user: state.user.user,
  error: state.error
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    { openOverlay, getLoggedUser, discardError, fetchUserNotifications, getLiveEvents },
    dispatch
  )
});

/* eslint-disable */
/* stylelint-disable */
const GlobalStyles = createGlobalStyle`
  ${reset}

  [hidden] {
    display: none !important;
  }

  .app {
    // overflow-x: hidden;
    min-height: 100vh;
    text-rendering: optimizeLegibility;
  }
`;
/* stylelint-enable */
/* eslint-enable */

type Props = {
  omniNavData?: Object,
  user: UserType,
  error: ErrorType,
  actions: Object,
  location: Object
};

class App extends React.Component<Props> {
  handleLoggedAction = (event: SyntheticUIEvent<>) => this.props.actions.openOverlay(event.detail);

  componentDidUpdate(prevProps) {
    const { location, error, actions } = this.props;
    const { location: prevLocation } = prevProps;

    if (prevLocation.pathname !== location.pathname && !location.hash) {
      window.scrollTo(0, 0);
      error.hasOwnProperty('status') && actions.discardError();
    }
  }

  componentDidMount() {
    if (!this.props.user.hasOwnProperty('isLoggedIn')) {
      this.props.actions.getLoggedUser();
    }

    const preventEvent = (event) => {
      const relativeParent = getRelativeParent(event.target);
      const isEditor =
        event.target.classList.value.includes('EditorContainer') ||
        (relativeParent && relativeParent.classList.value.includes('DraftEditor'));
      if (!isEditor) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // $FlowFixMe
    document.addEventListener('dragover', preventEvent);
    // $FlowFixMe
    document.addEventListener('drop', preventEvent);
    window.addEventListener('loggedAction', this.handleLoggedAction);
  }

  static loadInitialData(store, cookie) {
    const promises = [];
    promises.push(store.dispatch(getLoggedUser(cookie)));
    promises.push(store.dispatch(getLiveEvents()));
    return Promise.all(promises);
  }

  newsletterAction = (preferences: Object) => {
    this.props.actions.fetchUserNotifications(preferences);
  };

  render() {
    const { user, location, error, actions, omniNavData } = this.props;
    if (!user.hasOwnProperty('isLoggedIn')) return null;

    return (
      <ThemeProvider theme={theme}>
        <CookiesCMP user={user} location={location} />
        <AppContextual />

        <TrackableProvider>
          <GoogleSignIn />
          <InterstitialWithTargeting />

          <Main grey>
            <Overlay />
            {error.hasOwnProperty('status') ? <ErrorMessage discardError={actions.discardError} /> : null}
            <GlobalStyles />
            <OmniNav user={user} omniNavData={omniNavData} finectApp="front-questions" />
            <Content>
              <Switch>
                <Route path="/preguntas/temas/:tagId(\d+)/:tagSlug" component={Topic} />
                <Route exact path="/preguntas/:questionId(\d+)/:questionSlug" component={Question} />
                <Route exact path="/preguntas/nueva-pregunta" user={user} component={NewQuestion} />
                <Route exact path="/preguntas/temas" component={Topics} />
                <Route path="/preguntas" render={props => <Questions user={user} {...props} />} />
                <Route
                  render={props => <NotFound actionText="Ir a preguntas" redirectPath="/preguntas" {...props} />}
                />
              </Switch>
            </Content>

            <Footer currentApp="questions" user={user} newsletterAction={this.newsletterAction} />
          </Main>
        </TrackableProvider>
      </ThemeProvider>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
